import React, { createContext, useContext, useEffect, useState } from "react";
import { CampaignFilter, CampaignFilterDurationType } from "./models";

interface CampaignFilterContextProps {
  filter: CampaignFilter | null;
  setFilter: React.Dispatch<React.SetStateAction<CampaignFilter | null>>;
  amountFilterCheck: {
    lessThanAmountSelected: boolean;
    moreThanAmountSelected: boolean;
  };
  setAmountFilterCheck: React.Dispatch<
    React.SetStateAction<{
      lessThanAmountSelected: boolean;
      moreThanAmountSelected: boolean;
    }>
  >;
  clientFilterCheck: {
    newClientsSelected: boolean;
    notBookedInDaysSelected: boolean;
    loyalClientsSelected: boolean;
    loyalButLostClientsSelected: boolean;
  };
  setClientFilterCheck: React.Dispatch<
    React.SetStateAction<{
      newClientsSelected: boolean;
      notBookedInDaysSelected: boolean;
      loyalClientsSelected: boolean;
      loyalButLostClientsSelected: boolean;
    }>
  >;
  serviceFilterCheck: {
    usedOneServiceButNotOtherServiceSelected: boolean;
    leftOverServiceSelected: boolean;
    notTriedServiceSelected: boolean;
  };
  setServiceFilterCheck: React.Dispatch<
    React.SetStateAction<{
      usedOneServiceButNotOtherServiceSelected: boolean;
      leftOverServiceSelected: boolean;
      notTriedServiceSelected: boolean;
    }>
  >;
  clearCampaignFilter: () => void;
  fillupFilterDetails: (filter: CampaignFilter) => void;
}

const CampaignFilterContext = createContext<CampaignFilterContextProps | null>(
  null,
);

const durationFilterObject: CampaignFilter = {
  type: "duration",
  duration: {
    value: 30,
    unit: CampaignFilterDurationType.Days,
  },
  client_presence: {
    new_clients: false,
    not_booked_in_days: false,
  },
  filter_start_date: null,
  filter_end_date: null,
  gender: "all",
};

const customFilterObject: CampaignFilter = {
  type: "custom",
  client_presence: {
    loyal_clients: {
      min_bookings_per_year: null,
    },
    loyal_but_lost_clients: {
      min_booking_duration: null,
      booking_frequency: null,
      lost_duration: null,
    },
  },
  service_usage: {
    used_service_id: null,
    not_used_service_id: null,
    not_tried_service_id: null,
    left_over_details: {
      service_id: null,
      booking_frequency: null,
      since_duration: null,
    },
  },
  amount_spent: {
    more_than: null,
    less_than: null,
  },
  gender: "all",
};

export default function CampaignFilterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [filter, setFilter] = useState<CampaignFilter | null>(null);
  const [amountFilterCheck, setAmountFilterCheck] = useState({
    lessThanAmountSelected: false,
    moreThanAmountSelected: false,
  });

  const [clientFilterCheck, setClientFilterCheck] = useState({
    newClientsSelected: false,
    notBookedInDaysSelected: false,
    loyalClientsSelected: false,
    loyalButLostClientsSelected: false,
  });

  const [serviceFilterCheck, setServiceFilterCheck] = useState({
    usedOneServiceButNotOtherServiceSelected: false,
    leftOverServiceSelected: false,
    notTriedServiceSelected: false,
  });

  useEffect(() => {
    if (filter && filter.type === "duration") {
      setFilter(durationFilterObject);
    } else if (filter && filter.type === "custom") {
      setFilter(customFilterObject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.type]);

  const clearCampaignFilter = () => {
    setAmountFilterCheck({
      lessThanAmountSelected: false,
      moreThanAmountSelected: false,
    });
    setClientFilterCheck({
      newClientsSelected: false,
      notBookedInDaysSelected: false,
      loyalClientsSelected: false,
      loyalButLostClientsSelected: false,
    });
    setServiceFilterCheck({
      usedOneServiceButNotOtherServiceSelected: false,
      leftOverServiceSelected: false,
      notTriedServiceSelected: false,
    });
    setFilter(null);
  };

  const fillupFilterDetails = (filter: CampaignFilter) => {
    if (filter) {
      // Amount filter checks
      const newAmountFilterCheck = {
        lessThanAmountSelected:
          filter.amount_spent?.less_than !== null &&
          filter.amount_spent?.less_than !== undefined,
        moreThanAmountSelected:
          filter.amount_spent?.more_than !== null &&
          filter.amount_spent?.more_than !== undefined,
      };
      setAmountFilterCheck(newAmountFilterCheck);

      // Client filter checks
      const newClientFilterCheck = {
        newClientsSelected: filter.client_presence?.new_clients ?? false,
        notBookedInDaysSelected:
          filter.client_presence?.not_booked_in_days ?? false,
        loyalClientsSelected:
          filter.client_presence?.loyal_clients?.min_bookings_per_year !==
            null &&
          filter.client_presence?.loyal_clients?.min_bookings_per_year !==
            undefined,
        loyalButLostClientsSelected:
          (filter.client_presence?.loyal_but_lost_clients?.booking_frequency !==
            null &&
            filter.client_presence?.loyal_but_lost_clients
              ?.booking_frequency !== undefined) ||
          (filter.client_presence?.loyal_but_lost_clients
            ?.min_booking_duration !== null &&
            filter.client_presence?.loyal_but_lost_clients
              ?.min_booking_duration !== undefined) ||
          (filter.client_presence?.loyal_but_lost_clients?.lost_duration !==
            null &&
            filter.client_presence?.loyal_but_lost_clients?.lost_duration !==
              undefined),
      };
      setClientFilterCheck(newClientFilterCheck);

      // Service filter checks
      const newServiceFilterCheck = {
        usedOneServiceButNotOtherServiceSelected:
          filter.service_usage?.used_service_id !== null &&
          filter.service_usage?.used_service_id !== undefined &&
          filter.service_usage?.not_used_service_id !== null &&
          filter.service_usage?.not_used_service_id !== undefined,
        leftOverServiceSelected:
          filter.service_usage?.left_over_details?.service_id !== null &&
          filter.service_usage?.left_over_details?.service_id !== undefined,
        notTriedServiceSelected:
          filter.service_usage?.not_tried_service_id !== null &&
          filter.service_usage?.not_tried_service_id !== undefined,
      };
      setServiceFilterCheck(newServiceFilterCheck);
    }
  };

  return (
    <CampaignFilterContext.Provider
      value={{
        filter,
        setFilter,
        amountFilterCheck,
        setAmountFilterCheck,
        clientFilterCheck,
        setClientFilterCheck,
        serviceFilterCheck,
        setServiceFilterCheck,
        clearCampaignFilter,
        fillupFilterDetails,
      }}
    >
      {children}
    </CampaignFilterContext.Provider>
  );
}

export const useCampaignFilter = () => {
  const context = useContext(CampaignFilterContext);
  if (!context) {
    throw new Error(
      "useCampaignFilter must be used within CampaignFilterProvider",
    );
  }
  return context;
};
